export default class BaseScene extends Phaser.Scene {

    init() {
        this.crumbs = this.game.crumbs
        this.network = this.game.network
        this.soundManager = this.game.soundManager

        this.interface = this.scene.get('InterfaceController')
        this.memory = this.scene.get('MemoryManager')
        this.ruffle = this.scene.get('RuffleController')
        this.world = this.scene.get('WorldController')

        this.input.on('pointerover', () => this.interface.resetCursor(this))

        this.audioSelections = {}
    }

    getString(...args) {
        return args.map(id => this.crumbs.strings[id.toLowerCase()] || id).join(' ')
    }

    getFormatString(id, ...args) {
        return Phaser.Utils.String.Format(this.crumbs.strings[id.toLowerCase()], args)
    }

    setMusic() {
        this.setSceneAudio()
    }

    setSceneAudio(audioConfig = null) {
        if (!this.soundManager) {
            return
        }

        const config = audioConfig ?? this.getSceneAudio()
        this.soundManager.setSceneAudio(config)
    }

    playMusic(key) {
        this.soundManager?.playMusic(key)
    }

    stopMusic() {
        this.soundManager?.stopMusic()
    }

    preloadSceneAudio(loader) {
        if (!loader || !this.cache?.audio) {
            return
        }

        const config = this.getSceneAudio()

        for (const layer of ['music', 'bgs', 'ambience']) {
            const entry = config?.[layer]
            const key = entry?.key ?? entry

            if (key && !this.cache.audio.exists(key)) {
                loader.audio(key, `assets/media/music/${key}.mp3`)
            }
        }
    }

    unloadSceneAudio() {
        if (!this.memory || !this.soundManager) {
            return
        }

        const activeKeys = new Set(
            ['music', 'bgs', 'ambience']
                .map(layer => this.soundManager.getLayerKey(layer))
                .filter(Boolean)
        )

        for (const key of this.getSceneAudioKeys()) {
            if (activeKeys.has(key)) {
                continue
            }

            this.memory.unloadAudio(key)
        }
    }

    getSceneAudio(force = false) {
        const audio = {}

        for (const layer of ['music', 'bgs', 'ambience']) {
            const selection = this.selectAudioLayer(layer, this[layer], force)

            if (selection) {
                const volume = this.getLayerVolume(layer)
                const rate = this.getLayerRate(layer)
                const entry = { key: selection }

                if (volume !== null) {
                    entry.volume = volume
                }

                if (rate !== null) {
                    entry.rate = rate
                }

                audio[layer] = entry
            } else {
                audio[layer] = null
            }
        }

        if (audio.music?.key && audio.ambience?.key) {
            // Ease back music so ambience remains audible when both play together
            const desiredMusicVolume = 0.6
            const currentMusicVolume = audio.music.volume

            if (currentMusicVolume === undefined || currentMusicVolume === null || currentMusicVolume > desiredMusicVolume) {
                audio.music = { ...audio.music, volume: desiredMusicVolume }
            }

            if (audio.ambience.volume === undefined || audio.ambience.volume === null) {
                audio.ambience = { ...audio.ambience, volume: 1 }
            }
        }

        return audio
    }

    getSceneAudioKeys() {
        if (!this.audioSelections) {
            return []
        }

        const keys = []

        for (const layer of ['music', 'bgs', 'ambience']) {
            const key = this.audioSelections[layer]

            if (key) {
                keys.push(key)
            }
        }

        return [...new Set(keys)]
    }

    selectAudioLayer(layer, source, force = false) {
        if (!this.audioSelections) {
            this.audioSelections = {}
        }

        if (!force && Object.prototype.hasOwnProperty.call(this.audioSelections, layer)) {
            return this.audioSelections[layer]
        }

        const preferred = !force
            ? this.soundManager?.getLayerKey(layer) ?? null
            : null

        const selection = this.resolveAudioKey(source, preferred)
        this.audioSelections[layer] = selection

        return selection
    }

    resolveAudioKey(source, preferred = null) {
        if (source === undefined || source === null) {
            return null
        }

        if (Array.isArray(source)) {
            const pool = source
                .map(value => this.normalizeAudioValue(value))
                .filter(Boolean)

            if (pool.length === 0) {
                return null
            }

            const normalizedPreferred = this.normalizeAudioValue(preferred)

            if (normalizedPreferred && pool.includes(normalizedPreferred)) {
                return normalizedPreferred
            }

            const index = Math.floor(Math.random() * pool.length)
            return pool[index]
        }

        return this.normalizeAudioValue(source)
    }

    normalizeAudioValue(value) {
        if (typeof value === 'number') {
            if (!Number.isFinite(value) || value <= 0) {
                return null
            }

            return String(value)
        }

        if (typeof value === 'string') {
            const trimmed = value.trim()
            return trimmed.length > 0 ? trimmed : null
        }

        if (typeof value === 'object' && value !== null && 'key' in value) {
            return this.normalizeAudioValue(value.key)
        }

        return null
    }

    getLayerVolume(layer) {
        const prop = `${layer}Volume`
        const value = this[prop]

        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return null
        }

        return Math.max(0, Math.min(1, value))
    }

    getLayerRate(layer) {
        const prop = `${layer}Rate`
        const value = this[prop]

        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return null
        }

        return value > 0 ? value : null
    }

    stampEarned(stamp, flash = false) {
        if (!this.world?.client) {
            return
        }

        this.world.client.stampEarned(stamp, flash)
    }

    playLoopedMusic(preLoop, mainLoops) {
        const playLooped = this.soundManager && typeof this.soundManager.playLoopedMusic === 'function'

        if (playLooped) {
            this.soundManager.playLoopedMusic(preLoop, mainLoops)
            return
        }

        const fallbackKey = preLoop || (Array.isArray(mainLoops) && mainLoops.length > 0 ? mainLoops[0] : null)

        if (fallbackKey) {
            this.playMusic(fallbackKey)
        }
    }

    playLayeredMusic(keys) {
        this.soundManager?.playLayeredMusic(keys)
    }

    changeLayerMute(layer, state) {
        this.soundManager?.changeLayerMute(layer, state)
    }

    changeLayerMutes(layers) {
        this.soundManager?.changeLayerMutes(layers)
    }

    rewindLayers() {
        this.soundManager?.rewindLayers()
    }

}
